import React from 'react';

// ExplainedSetting — a self-documenting admin control (2026-08-06).
//
// The owner is not a betting-math expert; a bare number field like
// "decided_min_odds = 0" leaves them guessing. This control makes every
// game/risk-deciding setting explain ITSELF: 3 (or more) preset options +
// a Custom option, and — always visible — a plain-language panel describing
// exactly what the CURRENT choice does and its likely outcome (including the
// house-side impact). The owner picks a preset with confidence, or types a
// custom value and still sees, in words, what it will do.
//
// Reusable by design — wrap ANY game-deciding setting in it rather than a raw
// input. Class-based dark mode (dark:), mobile-first (chips wrap), matches the
// admin arena/neon palette.

export type PresetTone = 'recommended' | 'safe' | 'neutral' | 'risky';

export interface ExplainedPreset {
  label: string;                 // e.g. "Balanced"
  value: number | string | boolean;
  tagline?: string;              // small value hint on the chip, e.g. "1.05"
  consequence: string;          // plain-language: what this choice does + outcome
  tone?: PresetTone;            // colours the consequence panel + a chip dot
}

export interface ExplainedCustom {
  kind: 'number' | 'text';
  min?: number;
  max?: number;
  step?: number;
  unitPrefix?: string;          // e.g. "₹"
  unitSuffix?: string;          // e.g. "odds"
  placeholder?: string;
  describe: (v: any) => string; // consequence text for the typed value
}

export interface ExplainedSettingProps {
  title: string;
  help?: string;                // one-line "what is this"
  presets: ExplainedPreset[];
  value: any;
  onChange: (v: any) => void;
  custom?: ExplainedCustom;
  disabled?: boolean;
  /** Optional label for how the current value reads, e.g. "1.05" or "Off". */
  formatValue?: (v: any) => string;
}

const TONE: Record<PresetTone, { box: string; dot: string; icon: string }> = {
  recommended: {
    box: 'bg-violet-50 dark:bg-violet-500/10 border-violet-300/70 dark:border-violet-400/30 text-violet-900 dark:text-violet-100',
    dot: 'bg-violet-500', icon: '★',
  },
  safe: {
    box: 'bg-emerald-50 dark:bg-emerald-500/10 border-emerald-300/70 dark:border-emerald-400/30 text-emerald-900 dark:text-emerald-100',
    dot: 'bg-emerald-500', icon: '🛡️',
  },
  neutral: {
    box: 'bg-arena-50 dark:bg-arena-700/40 border-arena-200/70 dark:border-arena-600/50 text-arena-800 dark:text-neon-100',
    dot: 'bg-arena-400', icon: 'ℹ️',
  },
  risky: {
    box: 'bg-amber-50 dark:bg-amber-500/10 border-amber-300/70 dark:border-amber-400/30 text-amber-900 dark:text-amber-100',
    dot: 'bg-amber-500', icon: '⚠️',
  },
};

const sameValue = (a: any, b: any) => String(a) === String(b);

export default function ExplainedSetting(props: ExplainedSettingProps) {
  const { title, help, presets, value, onChange, custom, disabled, formatValue } = props;

  // Explicit custom-mode flag. We can't infer "is the Custom option selected?"
  // purely from "the value matches no preset" — because a value can legitimately
  // EQUAL a preset while the user is in custom mode (they clicked Custom to type
  // their own number that happens to start at a preset value). Without this flag,
  // clicking Custom while the value equals a preset would appear to do nothing.
  const [customMode, setCustomMode] = React.useState(false);
  const matchIdx = presets.findIndex((p) => sameValue(p.value, value));
  const isCustomActive = customMode || matchIdx === -1;
  const activePresetIdx = isCustomActive ? -1 : matchIdx;
  const activeTone: PresetTone = isCustomActive
    ? 'neutral'
    : (presets[activePresetIdx].tone || 'neutral');
  const tone = TONE[activeTone];

  const consequence = isCustomActive
    ? (custom ? custom.describe(value) : '')
    : presets[activePresetIdx].consequence;

  const currentLabel = formatValue
    ? formatValue(value)
    : (isCustomActive ? String(value ?? '') : (presets[activePresetIdx].tagline || String(value)));

  const chipBase =
    'relative text-left rounded-xl border px-3 py-2 transition active:scale-[0.98] focus:outline-none focus:ring-2 focus:ring-violet-400/50 disabled:opacity-50 disabled:cursor-not-allowed';
  const chipOn =
    'bg-violet-600 border-violet-500 text-white shadow-[0_2px_10px_rgba(139,92,246,0.35)]';
  const chipOff =
    'bg-white dark:bg-arena-700/40 border-arena-200/70 dark:border-arena-600/50 text-arena-800 dark:text-neon-100 hover:border-violet-400/60';

  return (
    <div className="rounded-2xl bg-white dark:bg-arena-800/40 ring-1 ring-inset ring-arena-200/60 dark:ring-arena-600/40 p-4 sm:p-5">
      <div className="flex items-baseline justify-between gap-3 flex-wrap">
        <h3 className="text-[14px] font-bold text-arena-900 dark:text-white">{title}</h3>
        <span className="text-[11px] font-semibold text-arena-500 dark:text-neon-200/70">
          Now: <span className="text-arena-800 dark:text-neon-100">{currentLabel}</span>
        </span>
      </div>
      {help && (
        <p className="mt-1 text-[12px] leading-snug text-arena-500 dark:text-neon-200/60">{help}</p>
      )}

      {/* Preset chips + Custom */}
      <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-2">
        {presets.map((p, i) => {
          const on = i === activePresetIdx;
          const pt = p.tone ? TONE[p.tone] : null;
          return (
            <button
              key={i}
              type="button"
              disabled={disabled}
              onClick={() => { setCustomMode(false); onChange(p.value); }}
              className={`${chipBase} ${on ? chipOn : chipOff}`}
            >
              <div className="flex items-center gap-2">
                {pt && <span className={`inline-block w-2 h-2 rounded-full ${on ? 'bg-white/80' : pt.dot}`} />}
                <span className="text-[13px] font-bold">{p.label}</span>
                {p.tagline && (
                  <span className={`ml-auto text-[12px] font-mono ${on ? 'text-white/90' : 'text-arena-500 dark:text-neon-200/70'}`}>
                    {p.tagline}
                  </span>
                )}
              </div>
              {p.tone === 'recommended' && (
                <span className={`mt-0.5 block text-[10px] font-bold uppercase tracking-wide ${on ? 'text-white/85' : 'text-violet-600 dark:text-violet-300'}`}>
                  ★ Recommended
                </span>
              )}
            </button>
          );
        })}

        {custom && (
          <button
            type="button"
            disabled={disabled}
            onClick={() => setCustomMode(true)}
            className={`${chipBase} ${isCustomActive ? chipOn : chipOff}`}
          >
            <span className="text-[13px] font-bold">Custom…</span>
            <span className={`ml-2 text-[11px] ${isCustomActive ? 'text-white/85' : 'text-arena-500 dark:text-neon-200/70'}`}>
              set your own
            </span>
          </button>
        )}
      </div>

      {/* Custom input (only when Custom is active) */}
      {custom && isCustomActive && (
        <div className="mt-3 flex items-center gap-2">
          {custom.unitPrefix && (
            <span className="text-[13px] font-bold text-arena-500 dark:text-neon-200/70">{custom.unitPrefix}</span>
          )}
          <input
            type={custom.kind === 'number' ? 'number' : 'text'}
            disabled={disabled}
            min={custom.min}
            max={custom.max}
            step={custom.step}
            placeholder={custom.placeholder}
            value={value ?? ''}
            onChange={(e) => {
              if (custom.kind === 'number') {
                const raw = e.target.value;
                onChange(raw === '' ? '' : Number(raw));
              } else {
                onChange(e.target.value);
              }
            }}
            className="w-full max-w-[220px] rounded-xl px-3 py-2 text-[14px] bg-white dark:bg-arena-700/40 border border-arena-200/60 dark:border-arena-600/50 text-arena-900 dark:text-white placeholder-arena-400 dark:placeholder-neon-200/55 focus:outline-none focus:ring-2 focus:ring-violet-400/40 focus:border-violet-400 disabled:opacity-50"
          />
          {custom.unitSuffix && (
            <span className="text-[12px] font-semibold text-arena-500 dark:text-neon-200/70">{custom.unitSuffix}</span>
          )}
        </div>
      )}

      {/* Always-visible consequence panel */}
      {consequence && (
        <div className={`mt-3 rounded-xl border px-3 py-2.5 text-[12.5px] leading-relaxed ${tone.box}`}>
          <span className="mr-1.5">{tone.icon}</span>
          {consequence}
        </div>
      )}
    </div>
  );
}
